'use client';

import { c as _c } from "react/compiler-runtime";
import { Centre, Heading, HStack, Text, VStack } from '@jaaq/hub-ui';
import { cn } from '@jaaq/hub-ui/cn';
import { Building2, GraduationCap, HeartPulse } from 'lucide-react';
import type { ReactElement } from 'react';
import { OnboardingShell } from '#components/onboarding-shell';
import { useOnboardingState } from '#lib/onboarding-provider';
import type { AudienceCategory, AudienceType } from '#lib/steps/state';
type Option = {
  id: AudienceType;
  label: string;
};
type Category = {
  id: AudienceCategory;
  icon: React.ReactNode;
  title: string;
  description: string;
  options: Option[];
};
const CATEGORIES: Category[] = [{
  id: 'education',
  icon: <GraduationCap className="size-6 text-fg" />,
  title: 'EDUCATION',
  description: 'Supporting students and academic staff',
  options: [{
    id: 'staff',
    label: 'Staff'
  }, {
    id: 'students',
    label: 'Students'
  }, {
    id: 'both',
    label: 'Both'
  }]
}, {
  id: 'healthcare',
  icon: <HeartPulse className="size-6 text-fg" />,
  title: 'HEALTHCARE',
  description: 'Supporting patients and healthcare professionals',
  options: [{
    id: 'staff',
    label: 'Staff'
  }, {
    id: 'patients',
    label: 'Patients'
  }, {
    id: 'both',
    label: 'Both'
  }]
}, {
  id: 'other-companies',
  icon: <Building2 className="size-6 text-fg" />,
  title: 'OTHER COMPANIES',
  description: 'Supporting employee wellbeing and development',
  options: [{
    id: 'staff',
    label: 'Employees'
  }]
}];
export const HostedAudienceStep = () => {
  const $ = _c(14);
  const {
    onboardingState,
    patch,
    goNext
  } = useOnboardingState();
  const selection = onboardingState.audience;
  let t0;
  if ($[0] !== patch) {
    t0 = function select(category, audienceType) {
      patch({
        audience: {
          category,
          audienceType
        }
      });
    };
    $[0] = patch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const select = t0;
  const t1 = Boolean(selection);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      index: 1,
      total: 2
    };
    t3 = <Heading level={1} variant="display" tone="inverse" className="max-w-160">Who will use your JAAQ Hosted platform?</Heading>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== select || $[5] !== selection?.audienceType || $[6] !== selection?.category) {
    t4 = CATEGORIES.map(cat => <VStack key={cat.id} gap={4} className="bg-surface flex-1 min-w-0 rounded-lg pt-5 px-5 pb-8"><Centre className="size-12 rounded-lg bg-black/5 shrink-0">{cat.icon}</Centre><VStack gap={2}><Heading level={2} variant="subtitle">{cat.title}</Heading><Text variant="body">{cat.description}</Text><VStack gap={2} className="mt-2" role="radiogroup" aria-label={cat.title}>{cat.options.map(opt => {
            const isSelected = selection?.category === cat.id && selection?.audienceType === opt.id;
            return <button key={opt.id} type="button" role="radio" aria-checked={isSelected} onClick={() => select(cat.id, opt.id)} className="w-full min-h-9 rounded-lg bg-surface-inverse text-fg-inverse flex items-center gap-2 px-5 py-2 hover:bg-surface-inverse/90 transition-colors"><RadioDot selected={isSelected} /><Text as="span" variant="body" tone="inverse" className="font-extrabold uppercase">{opt.label}</Text></button>;
          })}</VStack></VStack></VStack>);
    $[4] = select;
    $[5] = selection?.audienceType;
    $[6] = selection?.category;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <div className="bg-jaaq-grey-600 rounded-3xl p-5"><HStack wrap={true} gap={2.5}>{t4}</HStack></div>;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== goNext || $[11] !== t1 || $[12] !== t5) {
    t6 = <OnboardingShell canContinue={t1} onContinue={goNext} contentMaxWidth="1000px" customProgress={t2}>{t3}{t5}</OnboardingShell>;
    $[10] = goNext;
    $[11] = t1;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
};
type RadioDotProps = {
  selected: boolean;
};
const RadioDot = t0 => {
  const $ = _c(10);
  const {
    selected
  } = t0;
  const t1 = selected ? "text-jaaq-accent" : "text-fg-inverse";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("size-4 shrink-0", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = selected ? "#06b6d4" : "white";
  let t4;
  if ($[2] !== t3) {
    t4 = <circle cx="8" cy="8" r="7.5" fill={t3} stroke="#E5E5E5" />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== selected) {
    t5 = selected && <circle cx="8" cy="8" r="3" fill="white" />;
    $[4] = selected;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t2 || $[7] !== t4 || $[8] !== t5) {
    t6 = <svg viewBox="0 0 16 16" aria-hidden="true" className={t2}>{t4}{t5}</svg>;
    $[6] = t2;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};